





































































































import { Component, Prop, PropSync, Vue, Watch } from "vue-property-decorator";
import store from "@/store";
import _ from "underscore";
//  @types
import { Consequence } from "@/types/consequence";
import { LongTrendPeriodMeta } from "@/types/longTrendPeriodMeta";
import { ILongTrendPeriod } from "@/types/longTrendPeriod";
//  @clients
import ConsequencesClient from "@/Scripts/clients/consequences-client";
//  @modules
import { getModule } from "vuex-module-decorators";
import LongTrendPeriodModule from "@/store/clients/LongTrendPeriod.module";
import VesselsModule from "@/store/clients/Vessels.module";
import SnackbarModule from "@/store/clients/Snackbar.module";

const LongTrendPeriod = getModule(LongTrendPeriodModule, store);
const Vessels = getModule(VesselsModule, store);
const Snackbar = getModule(SnackbarModule, store);

@Component({})
export default class PerformanceImpactModal extends Vue {
  @PropSync("value", { required: true, type: Boolean, default: false }) isModalActive!: boolean;
  @Prop({ type: String, default: "460px" }) width!: string;
  @Prop({ default: true }) loading!: boolean;

  // @Data
  consequences: Consequence[] = [];
  consequencesLoading = true;
  draft: number = this.draftValue || 0;
  doDraftUpdateDebounced = _.debounce((value: number) => this.updateDraft(value), 2000);

  //  @Watchers
  @Watch("loading", { immediate: true })
  onLoaded(val: boolean): void {
    if (!val) {
      this.draft = this.draftValue || 0;
      this.fetchConsequences();
    }
  }

  //  @Getters
  get longTrendPeriod(): ILongTrendPeriod | null {
    return LongTrendPeriod.longTrendPeriod;
  }

  get isBenchmarking(): boolean | undefined {
    return this.longTrendPeriod?.isBenchmarking;
  }

  get showAsteriskText(): boolean {
    const type = this.diagnosticTypeName?.toLowerCase();
    return type === "speed" || type === "sfr";
  }

  get meta(): LongTrendPeriodMeta | null {
    return LongTrendPeriod.meta;
  }

  get draftValue(): number | null | undefined {
    return this.longTrendPeriod?.draft;
  }

  get startDate(): string | undefined {
    return this.meta?.startDate;
  }

  get endDate(): string | undefined {
    return this.meta?.endDate;
  }

  get diagnosticTypeName(): string | undefined {
    return this.longTrendPeriod?.diagnosticTypeName;
  }

  get hasConsequenceResults(): boolean {
    if (!this.consequences?.length) return false;
    return Boolean(this.consequences[0].results.length);
  }

  isSpeedChangeNegative(value: string): boolean {
    if (!value) return false;
    return Number(value.split(" knots")[0]) < 0;
  }

  isFuelOilImpactNegative(value: string): boolean {
    if (!value) return false;
    return Number(value.split(" MT/day")[0]) < 0;
  }

  get headers(): any[] {
    const headers: any[] = [];

    if (!this.consequences[0].results.length) return headers;

    headers.push({ text: "MCR", value: "MCR" });

    this.consequences[0].results.forEach(result => {
      headers.push({ text: result.headerName, value: result.headerName.split(/\W+/).join("") });
    });

    return headers;
  }

  get rows(): any[] {
    const rows: any[] = [];

    this.consequences.forEach(consequence => {
      const item: any = { MCR: consequence.mcrDisplay };

      consequence.results.forEach(result => {
        item[result.headerName.split(/\W+/).join("")] = result.displayValue;
      });
      rows.push(item);
    });

    return rows;
  }

  //  @Methods
  close(): void {
    this.isModalActive = false;
  }

  clear(): void {
    this.isModalActive = false;
    this.consequences = [];
    this.$emit("clear");
  }

  async fetchConsequences(): Promise<void> {
    try {
      this.consequences = await ConsequencesClient.find(Vessels.currentVessel?.id, this.longTrendPeriod?.longTrendId, this.startDate, this.endDate, this.longTrendPeriod?.benchmarkPeriod);
    } catch (error) {
      console.error("Error fetching consequences:", error);
    } finally {
      this.consequencesLoading = false;
    }
  }

  onDraftInput(): void {
    this.$nextTick(() => {
      const input = this.$refs.draftInput as any;
      if (input && typeof input.validate === "function" && input.validate() === true) {
        this.doDraftUpdateDebounced(this.draft);
      }
    });
  }

  async updateDraft(newValue: number): Promise<void> {
    if (!this.meta) {
      Snackbar.showSnackbar({ text: "Vessel meta data not available" });
      return;
    }
    this.draft = Number(newValue);
    const data = {
      vesselId: this.meta.vesselId,
      draft: this.draft,
    };
    try {
      await Vessels.updateVesselDraft(data);
      Snackbar.showSnackbar({
        text: `Draft successfully updated to <b>${this.draft} m</b>`,
        color: "ContentPrimaryStatic",
      });
    } catch (error) {
      Snackbar.showSnackbar({ text: "Failed to update draft" });
    } finally {
      this.$emit("update");
    }
  }

  draftInputRules(value: number): boolean | string {
    if (value === null || value === undefined) {
      return "Draft is required";
    }
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return "Draft must be a number";
    }
    if (value < 2 || value > 25) {
      return "Draft should be between 2 and 25";
    }
    return true;
  }
}
